<template>
  <ComponentDrawer
    :base-stacking-context="baseStackingContext"
    :componentType="selectedComponentType!"
    :extended-title="t('canvasUI.canvasBuilder.components.settings')"
    @update:show="updateShow"
  >
    <NForm :model="formValues">
      <NGrid :cols="1" :yGap="24">
        <NFormItemGridItem
          :feedback="t('canvasUI.canvasBuilder.canvasSettings.form.messages.customName')"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.customName')"
          path="trackingId"
        >
          <NInput v-model:value="formValues.trackingId" clearable placeholder="" @keyup.enter="updateShow(false)" />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="activeSettingsNode?.type === ComponentTypes.CollectionPlayer"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.height')"
          path="trackingId"
          :showFeedback="false"
          @keyup.enter="updateShow(false)"
        >
          <NInput
            v-model:value="formValues.height"
            clearable
            :placeholder="t('canvasUI.canvasBuilder.canvasSettings.form.placeholder.height')"
          />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="isCanvas || isCanvasTemplate"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.homePageSettings')"
          :show-feedback="false"
        >
          <CanvasHomeSettings v-if="activeSettingsNode" />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="canSelectSectionListStrategy"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.selectSectionListStrategy')"
          :show-feedback="false"
        >
          <SectionListStrategySelector
            v-model="formValues.sectionListSelection"
            :canvas-id="activeCanvas?.id"
            :section-list-data="activeSettingsNode?.data"
            :section-list-id="activeSettingsNode?.id"
          />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="allowEditOrRemove"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.canEditOrRemove')"
          path="canEditOrRemove"
          :show-feedback="false"
        >
          <CSelect v-model:value="formValues.canEditOrRemove" :bordered="true" :options="editOrRemoveOptions" />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="activeSettingsNode?.type === 'ContentGrid'"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.autofill')"
          :show-feedback="false"
        >
          <div class="flex flex-col flex-start gap-1 w-full">
            <NSelect
              v-model:value="formValues.autofill as string"
              bordered
              :clearable="false"
              :options="autofillOptions"
            />

            <NFormItemGridItem
              :feedback="t('canvasUI.canvasBuilder.canvasSettings.form.messages.matchingMetadataAutofillContentTypes')"
              :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.matchingMetadataAutofillContentTypes')"
            >
              <NSelect
                v-model:value="formValues.autofillContentTypes"
                bordered
                :disabled="
                  !['matching_metadata', 'matching_tags', 'matching_metadata_or_tags'].includes(formValues.autofill)
                "
                multiple
                :options="contentTypeOptions"
                :placeholder="
                  t('canvasUI.canvasBuilder.canvasSettings.form.placeholder.matchingMetadataAutofillContentTypes')
                "
              />
            </NFormItemGridItem>

            <NCheckbox v-model:checked="formValues.isShareboxTarget" class="mt-2">
              {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.isShareboxTarget') }}
            </NCheckbox>
          </div>
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="isAdminSectionTemplateWithFlag"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.editability')"
          :show-feedback="false"
        >
          <div class="flex flex-col flex-start gap-1">
            <NCheckbox v-model:checked="formValues.allowAdminsToOverwrite">
              {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.allowAdminsToOverwrite') }}
            </NCheckbox>
          </div>
        </NFormItemGridItem>

        <NFormItemGridItem
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.visibility')"
          :show-feedback="false"
        >
          <div class="flex flex-col flex-start gap-1">
            <NCheckbox v-if="isAdmin && !isPageBreakComponent" v-model:checked="formValues.visibleInEditMode">
              {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.visibleInEditMode') }}
            </NCheckbox>

            <NCheckbox v-if="!isPageBreakComponent" v-model:checked="formValues.visibleInPresentationMode">
              {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.visibleInPresentationMode') }}
            </NCheckbox>

            <NCheckbox v-if="!isPageBreakComponent" v-model:checked="formValues.visibleInWeb">
              {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.visibleInWeb') }}
            </NCheckbox>

            <NCheckbox v-if="formValues.visibleInWeb || isPageBreakComponent" v-model:checked="formValues.visibleInPdf">
              {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.visibleInPdf') }}
            </NCheckbox>
          </div>
        </NFormItemGridItem>
        <NFormItemGridItem
          v-if="showScaleToFitPageControl"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.messages.printSettings')"
          :show-feedback="false"
        >
          <NCheckbox v-model:checked="formValues.scale_to_fit_page">
            {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.scaleToFitPage') }}
          </NCheckbox>
        </NFormItemGridItem>
        <NFormItemGridItem
          v-if="canBeLinkedInTemplates"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.messages.canvasAnchoringSettings')"
          :show-feedback="false"
        >
          <NCheckbox v-model:checked="formValues.linkable_in_templates">
            {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.linkable') }}
          </NCheckbox>
        </NFormItemGridItem>
        <NFormItemGridItem
          v-if="canSelectAnimation"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.selectTransition')"
          :show-feedback="false"
        >
          <CAnimationSelector v-model="formValues.animation" />
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="activeSettingsNode?.type === ComponentTypes.CollectionPlayer"
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.collectionPlayer')"
          :show-feedback="false"
        >
          <div class="flex flex-col flex-start gap-1 w-full">
            <NCheckbox v-model:checked="formValues.hideFooter">
              {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.hideBottomBarWithControls') }}
            </NCheckbox>

            <NCheckbox v-model:checked="formValues.isShareboxTarget">
              {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.isShareboxTarget') }}
            </NCheckbox>

            <div
              v-if="
                launchDarkly.enable_collection_player_data_accessor && (isTemplate || selectedCollectionPlayerDataPath)
              "
              class="mt-2"
            >
              <div class="flex items-center gap-2 mb-1">
                <label>{{ t('canvasUI.canvasBuilder.canvasSettings.form.label.collectionPlayerDataAccessor') }}</label>
                <CHelpText>
                  {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.collectionPlayerDataAccessorHelp') }}
                </CHelpText>
              </div>

              <NTreeSelect
                v-if="!!crmShape && isTemplate"
                block-line
                clearable
                filterable
                :on-update:value="handleCollectionPlayerDataSelection"
                :options="collectionPlayerTreeData"
                :override-default-node-click-behavior="({ option }) => (option.children ? 'toggleExpand' : 'default')"
                :value="selectedCollectionPlayerDataPath"
              />

              <div v-if="selectedCollectionPlayerDataPath" class="mt-2 pa-2 bg-primary6 rounded-1">
                <code class="text-xs color-primary">{{ selectedCollectionPlayerDataPath }}</code>
              </div>
            </div>
          </div>
        </NFormItemGridItem>

        <NFormItemGridItem
          v-if="
            activeSettingsNode?.type === 'ContentGrid' &&
            launchDarkly.enable_content_grid_data_accessor &&
            (isTemplate || selectedContentGridDataPath)
          "
          :label="t('canvasUI.canvasBuilder.canvasSettings.form.label.contentGrid')"
          :show-feedback="false"
        >
          <div class="flex flex-col flex-start gap-1 w-full">
            <div class="mt-2">
              <div class="flex items-center gap-2 mb-1">
                <label>{{ t('canvasUI.canvasBuilder.canvasSettings.form.label.contentGridDataAccessor') }}</label>
                <CHelpText>
                  {{ t('canvasUI.canvasBuilder.canvasSettings.form.messages.contentGridDataAccessorHelp') }}
                </CHelpText>
              </div>

              <NTreeSelect
                v-if="!!crmShape && isTemplate"
                block-line
                clearable
                filterable
                :on-update:value="handleContentGridDataSelection"
                :options="contentGridTreeData"
                :override-default-node-click-behavior="({ option }) => (option.children ? 'toggleExpand' : 'default')"
                :value="selectedContentGridDataPath"
              />

              <div v-if="selectedContentGridDataPath" class="mt-2 pa-2 bg-primary6 rounded-1">
                <code class="text-xs color-primary">{{ selectedContentGridDataPath }}</code>
              </div>
            </div>
          </div>
        </NFormItemGridItem>
      </NGrid>
    </NForm>

    <CButton block class="mt-auto" type="primary" @click="updateShow(false)">{{ t('canvasUI.common.done') }}</CButton>
  </ComponentDrawer>
</template>

<script setup lang="ts">
import { reactive, computed, ref, onMounted, inject, type ComputedRef } from 'vue'
import { cloneDeep, isEqual } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { NForm, NInput, NGrid, NFormItemGridItem, NCheckbox, NSelect, NTreeSelect } from 'naive-ui'

import SectionListStrategySelector from '@canvas-builder/components/ui/SectionList/SectionListStrategySelector.vue'
import ComponentDrawer from '@canvas-builder/components/container/ComponentDrawer/ComponentDrawer.vue'
import CanvasHomeSettings from '@canvas-builder/components/container/ComponentDrawerSettings/CanvasHomeSettings.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import CButton from '@lib/components/CButton/CButton.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CAnimationSelector from '@lib/components/CAnimationSelector/CAnimationSelector.vue'
import CHelpText from '@lib/components/CHelpText/CHelpText.vue'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { transformToArrayTree } from '@lib/util/tree-transformation.util'
import type { AppFile } from '@lib/types/app'
import { FileContentTypeEnum, FileTypeEnum } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import {
  type SectionListProps,
  type ComponentNode,
  ComponentTypes,
  type ContentGridProps,
} from '@canvas-builder/types/canvas'
import type { CollectionPlayer as CollectionPlayerProps } from '@collection-player/types/collection-player.type'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

interface Props {
  baseStackingContext?: number
}

defineProps<Props>()

const { t } = useI18n()
const {
  isAdmin,
  setComponentSettingsMode,
  selectedComponentType,
  patchNodeById,
  activeSettingsNode,
  activeCanvas,
  listFiles,
  isCanvasTemplate,
  isCanvas,
  isTemplate,
  isSection,
} = useCanvas()

const { getComponentDefaultVisibility } = useCanvasVisibility()
const { crmShape } = useCrmShape()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const isPageBreakComponent = computed(() => activeSettingsNode.value?.type === ComponentTypes.PageBreak)
const isGridComponent = computed(() => activeSettingsNode.value?.type === ComponentTypes.Grid)
const isSectionOrContentList = computed(() =>
  [ComponentTypes.SectionList, ComponentTypes.ContentList].some((type) => type === activeSettingsNode.value?.type),
)
const canBeLinkedInTemplates = computed(() => !isSectionOrContentList.value)
const canSelectAnimation = computed(
  () =>
    isSectionOrContentList.value && (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') === 'pagination',
)
const showScaleToFitPageControl = computed(
  () =>
    isGridComponent.value &&
    formValues.visibleInPdf &&
    !!launchDarkly.value.enable_scale_content &&
    pitcherSettings.value.default_pdf_page_dimensions?.width &&
    pitcherSettings.value.default_pdf_page_dimensions?.height,
)
const areSectionsSystemControlled = computed(() => launchDarkly.value?.are_sections_system_controlled)
const allowEditOrRemove = computed(
  () =>
    isAdmin.value &&
    (((selectedComponentType.value === ComponentTypes.Text ||
      selectedComponentType.value === ComponentTypes.Carousel ||
      selectedComponentType.value === ComponentTypes.Multimedia) &&
      isSection.value) ||
      (launchDarkly.value.enable_template_component_permissions && isCanvasTemplate.value)),
)
const canSelectSectionListStrategy = computed(
  () => isAdmin.value && isCanvasTemplate.value && isSectionOrContentList.value,
)

const isAdminSectionTemplateWithFlag = computed(
  () => isAdmin.value && isSection.value && isTemplate.value && areSectionsSystemControlled.value,
)

const collectionPlayerTreeData = computed(() => transformToArrayTree(crmShape.value ?? {}))
const contentGridTreeData = computed(() => transformToArrayTree(crmShape.value ?? {}))

const initialFormValues = {
  linkable_in_templates: activeSettingsNode.value?.linkable?.linkable_in_templates ?? false,
  trackingId: activeSettingsNode.value?.tracking_id ?? activeSettingsNode.value!.type,
  canEditOrRemove: getEditOrRemoveOption(),
  visibleInEditMode: activeSettingsNode.value?.visible?.edit_mode ?? true,
  visibleInPresentationMode: activeSettingsNode.value?.visible?.presentation_mode ?? true,
  visibleInWeb: activeSettingsNode.value?.visible?.web ?? true,
  visibleInPdf:
    activeSettingsNode.value?.visible?.pdf ?? getComponentDefaultVisibility(activeSettingsNode.value!.type, 'pdf'),
  scale_to_fit_page: activeSettingsNode.value?.print?.scale_to_fit_page ?? false,
  autofill: activeSettingsNode.value?.autofill || ('off' satisfies ComponentNode['autofill']),
  autofillContentTypes: activeSettingsNode.value?.autofill_content_types
    ? [...activeSettingsNode.value.autofill_content_types]
    : null,
  allowAdminsToOverwrite: activeSettingsNode.value?.allow_admins_to_overwrite ?? false,
  animation: canSelectAnimation.value
    ? ((activeSettingsNode.value?.data as SectionListProps)?.animation_in ?? 'none')
    : undefined,
  sectionListSelection: isSectionOrContentList.value
    ? {
        strategy: (activeSettingsNode.value?.data as SectionListProps)?.selection_strategy ?? 'free',
        appName: (activeSettingsNode.value?.data as SectionListProps)?.selection_app_name,
        is_restricted_to_preselections:
          (activeSettingsNode.value?.data as SectionListProps)?.is_restricted_to_preselections ?? false,
      }
    : null,
  ...(activeSettingsNode.value?.type === ComponentTypes.CollectionPlayer
    ? {
        hideFooter: (activeSettingsNode.value?.data as CollectionPlayerProps)?.hide_footer ?? false,
        height: activeSettingsNode.value?.height ?? '',
      }
    : {}),
  collectionPlayerDataAccessor: (activeSettingsNode.value?.data as CollectionPlayerProps)?.data_accessor ?? '',
  contentGridDataAccessor: (activeSettingsNode.value?.data as ContentGridProps)?.data_accessor ?? '',
  isShareboxTarget: activeSettingsNode.value?.is_sharebox_target ?? false,
}

const formValues = reactive(cloneDeep(initialFormValues))
const formHasChanges = computed<boolean>(() => !isEqual(formValues, initialFormValues))
const installedApps = ref<AppFile[]>([])
const hasAiAutofillAppInstalled = computed<boolean>(() =>
  installedApps.value.some((app) => ['ai-content', 'ai-sections'].some((tag) => app.app_metadata?.tags?.includes(tag))),
)
const selectedCollectionPlayerDataPath = ref<string>(
  (activeSettingsNode.value?.data as CollectionPlayerProps)?.data_accessor ?? '',
)
const selectedContentGridDataPath = ref<string>(
  (activeSettingsNode.value?.data as ContentGridProps)?.data_accessor ?? '',
)

function updateShow(show: boolean) {
  if (show) return
  formHasChanges.value && updateComponent()
  setComponentSettingsMode()
}

function handleCollectionPlayerDataSelection(value: string | null) {
  selectedCollectionPlayerDataPath.value = value || ''
  formValues.collectionPlayerDataAccessor = value || ''
}

function handleContentGridDataSelection(value: string | null) {
  selectedContentGridDataPath.value = value || ''
  formValues.contentGridDataAccessor = value || ''
}

function getEditOrRemoveOption() {
  // Use explicit permissions or fall back to the appropriate default
  // For template components with the feature flag, default to "can edit or remove"
  const defaultPermissions =
    isCanvasTemplate.value && launchDarkly.value.enable_template_component_permissions
      ? { is_editable: true, is_removable: true }
      : cloneDeep(defaultWhenUsedInSection)

  const map = activeSettingsNode.value?.when_used_in_section ?? defaultPermissions

  let value = ''

  value += map.is_editable ? '1' : '0'
  value += map.is_removable ? '1' : '0'

  return value
}

const editOrRemoveOptions = [
  {
    value: '00',
    map: { is_editable: false, is_removable: false },
    label: t('canvasUI.canvasBuilder.canvasSettings.form.options.canEditOrRemove.cannotEditOrRemove'),
  },
  {
    value: '10',
    map: { is_editable: true, is_removable: false },
    label: t('canvasUI.canvasBuilder.canvasSettings.form.options.canEditOrRemove.canEdit'),
  },
  {
    value: '01',
    map: { is_editable: false, is_removable: true },
    label: t('canvasUI.canvasBuilder.canvasSettings.form.options.canEditOrRemove.canRemove'),
  },
  {
    value: '11',
    map: { is_editable: true, is_removable: true },
    label: t('canvasUI.canvasBuilder.canvasSettings.form.options.canEditOrRemove.canEditOrRemove'),
  },
]

type AutofillValue = Exclude<ComponentNode['autofill'], undefined>
type AutofillOption = { label: string; value: AutofillValue }

const autofillOptions = computed((): AutofillOption[] => [
  {
    label: t('canvasUI.canvasBuilder.canvasSettings.form.messages.autofillOff'),
    value: 'off',
  },
  {
    label: t('canvasUI.canvasBuilder.canvasSettings.form.messages.matchingMetadataAutofill'),
    value: 'matching_metadata',
  },
  {
    label: t('canvasUI.canvasBuilder.canvasSettings.form.messages.matchingTagsAutofill'),
    value: 'matching_tags',
  },
  {
    label: t('canvasUI.canvasBuilder.canvasSettings.form.messages.matchingMetadataOrTagsAutofill'),
    value: 'matching_metadata_or_tags',
  },
  ...(hasAiAutofillAppInstalled.value
    ? ([
        { label: t('canvasUI.canvasBuilder.canvasSettings.form.messages.aiAutofill'), value: 'ai' },
      ] satisfies AutofillOption[])
    : []),
])

const contentTypeOptions = [
  { value: FileContentTypeEnum.AR, label: t('canvasUI.contentType.ar') },
  { value: FileContentTypeEnum.PDF, label: t('canvasUI.contentType.pdf') },
  { value: FileContentTypeEnum.VIDEO, label: t('canvasUI.contentType.video') },
  { value: FileContentTypeEnum.WEB, label: t('canvasUI.contentType.web') },
]

const isEmpty = (value: string) => value.trim() === ''

const updateComponent = () => {
  const tracking_id = isEmpty(formValues.trackingId) ? activeSettingsNode.value!.type : formValues.trackingId
  const when_used_in_section = editOrRemoveOptions.find((option) => option.value === formValues.canEditOrRemove)?.map
  if (!activeSettingsNode.value) {
    console.warn('[Component Drawer Settings]: No active settings node')
    return
  }

  let newData: { data?: any } = {} // component specific data
  const nodeData = activeSettingsNode.value?.data

  if (
    canSelectAnimation.value &&
    (nodeData as SectionListProps)?.animation_in !== formValues.animation &&
    !((nodeData as SectionListProps)?.animation_in === undefined && formValues.animation === 'off')
  ) {
    newData = { data: { ...nodeData, animation_in: formValues.animation } as SectionListProps }
  }

  if (formValues.sectionListSelection?.strategy) {
    newData = {
      data: {
        ...(newData.data || nodeData),
        selection_strategy: formValues.sectionListSelection.strategy,
        selection_app_name: formValues.sectionListSelection.appName,
        is_restricted_to_preselections: formValues.sectionListSelection.is_restricted_to_preselections,
      } as SectionListProps,
    }
  }

  if (activeSettingsNode.value?.type === ComponentTypes.CollectionPlayer) {
    newData = {
      data: {
        ...(newData.data || nodeData),
        hide_footer: formValues.hideFooter,
        data_accessor: formValues.collectionPlayerDataAccessor,
      } as CollectionPlayerProps,
    }
  }

  if (activeSettingsNode.value?.type === 'ContentGrid') {
    newData = {
      data: {
        ...(newData.data || nodeData),
        data_accessor: formValues.contentGridDataAccessor,
      } as ContentGridProps,
    }
  }

  patchNodeById(activeSettingsNode.value.id, {
    tracking_id,
    when_used_in_section,
    visible: {
      edit_mode: formValues.visibleInEditMode,
      presentation_mode: formValues.visibleInPresentationMode,
      web: formValues.visibleInWeb,
      pdf: formValues.visibleInPdf,
    },
    print: {
      scale_to_fit_page: formValues.scale_to_fit_page,
    },
    linkable: {
      linkable_in_templates: formValues.linkable_in_templates,
    },
    autofill: formValues.autofill,
    autofill_content_types: formValues.autofillContentTypes,
    // Add height only if already present or if the form value is set (backwards compatibility)
    ...(activeSettingsNode.value?.type === ComponentTypes.CollectionPlayer &&
    ('height' in activeSettingsNode.value || formValues.height)
      ? { height: formValues.height }
      : {}),
    ...(isAdminSectionTemplateWithFlag.value ? { allow_admins_to_overwrite: formValues.allowAdminsToOverwrite } : {}),
    ...(activeSettingsNode.value?.type &&
    ['ContentGrid', 'CollectionPlayer'].includes(activeSettingsNode.value.type as string)
      ? { is_sharebox_target: formValues.isShareboxTarget }
      : {}),
    ...newData,
  })
}

async function fetchApps() {
  let appList: AppFile[] = []

  try {
    appList = (await listFiles.value?.({
      filters: { type: [FileTypeEnum.APP] },
    })) as AppFile[]
  } catch (e) {
    console.warn('[Component Drawer Settings]: Could not fetch apps', e)
  }

  installedApps.value = appList.filter(
    (a) =>
      a.app_metadata?.module?.canvas?.enabled || a.app_metadata?.module?.dsr?.enabled || a.app_metadata?.hooks?.canvas,
  )
}

onMounted(() => {
  fetchApps()
})
</script>

<style scoped>
.data-accessor-input :deep(.n-input__prefix) {
  margin-right: 0;
}
</style>
